import React from 'react'
import {
  Anchor,
  ConfirmDialog,
  Dialog,
  Flex,
  LoadingSpinner,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import {
  useActions,
  useCallback,
  useFlagsWithUseQueries,
  useSelector,
  useState,
  useSubscriptions,
  useVertical,
} from 'src/hooks'
import { UiCard } from 'src/chrome'

import { LEGACY_SITE_URL } from 'src/app/api'
import { ButtonColorChoices } from 'src/theme/buttonColors'
import { CurrentPlanCard } from './components/CurrentPlanCard'
import { PaymentSettingsCard } from './components/PaymentSettings/PaymentSettingsCard'
import {
  FullAccessFeatureType,
  Label,
  StripeSubscriptionWithItemsFragment,
} from 'src/graphql/generated/graphql'
import {
  useAccountQuery,
  useCancelStripeSubscription,
  useGetSubscriptions,
  useDefaultSubscriptionIds,
  useUpdateAccount,
  useSubscriptionAddonRemove,
  useSubscriptionAddonAdd,
  useMarketingContent,
} from 'src/react_query'
import { AccountSubscriptionChanger } from 'src/SubscriptionChanger/AccountSubscriptionChanger'
import { Addons } from 'src/dashboard/components'
import { offeredAffiliateStripeSkus } from 'src/helpers/supportedUpsaleProducts'
import { AFFILIATE_BRANDING } from 'src/app/constants'
import { useFeatureAccess } from 'src/hooks/useFeatureAccess'
import {
  ACT_ADDON_STRIPE_ID,
  currentlySoldPlans,
} from 'src/marketing/plans/constants'
/**
 *
 * TODO:
 *  1. My Subscriptions Component
 * [] - New Designs for My Subscriptions
 * [] - Wire up route
 */

export const PlansAndPaymentView = () => {
  const [isChangingSubscription, setIsChangingSubscription] = useState(false)
  const [isChangingAffiliate, setIsChangingAffiliate] = useState(false)
  const { data } = useDefaultSubscriptionIds({
    suspense: true,
  })

  const defaultSubscriptionIds = data ?? []
  const [subscriptionCancelationId, setSubscriptionCancelationId] = useState<
    string | undefined
  >(undefined)
  const [subscriptionItemCancelationId, setSubscriptionItemCancelationId] =
    useState<string | undefined>(undefined)
  const [
    isRenewAffiliateConfirmDialogueOpen,
    setIsRenewAffiliateConfirmDialogueOpen,
  ] = useState(false)
  const [addingAddonToSubscriptionId, setAddingAddonSubscriptionId] = useState<
    string | undefined
  >(undefined)
  const [isShowingChangeCancelation, setIsShowingChangeCancelation] =
    useState(false)
  const { mutate: cancelSubscription, isLoading } =
    useCancelStripeSubscription()
  const vertical = useVertical()
  const isMobile = useSelector(state => state.window.isMobile)
  const actions = useActions()
  const { hasFeatureAccess: hasLifetimeAct } = useFeatureAccess([
    FullAccessFeatureType.UnlimitedActsSubDiscount,
  ])

  const updateAccount = useUpdateAccount()
  const { data: account, isLoading: isAccountLoading } = useAccountQuery()
  const { data: subscriptions, isLoading: isSubscriptionsLoading } =
    useGetSubscriptions()
  const { data: marketingContent } = useMarketingContent()
  const { canAddActAddon } = useFlagsWithUseQueries()
  const subscriptionAddonAdd = useSubscriptionAddonAdd()
  const subscriptionAddonRemove = useSubscriptionAddonRemove()

  const cancelOrUncancelSubscription = useCallback(
    async (subscriptionId: string, shouldCancel?: boolean) => {
      const sub = {
        id: subscriptionId,
        cancelAtPeriodEnd: shouldCancel ?? false,
      }

      try {
        const {
          updateAccount: { account: updatedAccount },
        } = await updateAccount.mutateAsync({
          account: {},
          subscription: sub,
        })
        actions.updatedAccount(updatedAccount)
      } catch (error) {
        console.error(error?.toString() ?? 'Failed to change subscription.')
      } finally {
        setIsChangingSubscription(false)
      }
    },
    [actions, updateAccount],
  )

  const currentPlan = account?.plan

  const rawCurrentPlanSubscription = subscriptions?.find(
    (sub: StripeSubscriptionWithItemsFragment) =>
      sub.sku === currentPlan?.stripeId,
  )
  // While the subscriptions don't have the ACT Addon with the lifetime discount, we're adding it
  //  here so we show it as they technically have it.
  // TODO: remove this once all users with the lifetime subscriptions actually have the addon
  const currentPlanSubscription =
    hasLifetimeAct && rawCurrentPlanSubscription?.items.length === 1
      ? {
          ...rawCurrentPlanSubscription,
          items: rawCurrentPlanSubscription.items.concat([
            {
              id: '',
              name: 'ACT Add-On',
              sku: ACT_ADDON_STRIPE_ID,
              isAddon: true,
              undiscountedAmount: 1000,
              amount: 0,
              discounts: [
                {
                  name: 'LifetimeACT',
                },
              ],
            },
          ]),
        }
      : rawCurrentPlanSubscription
  const currentAffiliateSubscription = subscriptions?.find(
    (sub: StripeSubscriptionWithItemsFragment) =>
      offeredAffiliateStripeSkus.map(sku => sku.id).includes(sub.sku),
  )

  const { newPlan, isInPromptingsGracePeriod } = useSubscriptions()

  const subscriptionIds = defaultSubscriptionIds.map(id => ({ id }))

  const changePlanId =
    newPlan && subscriptionIds.find(sub => sub.id === newPlan.id)

  if (isAccountLoading || isSubscriptionsLoading) {
    return <LoadingSpinner size="large" />
  }
  return account ? (
    <Flex width="100%" flexDirection="row" flexWrap="wrap">
      <Flex flexDirection={'column'} width="100%">
        <Text
          type="largeBody"
          weight="bold"
          color="primaryHeading"
          content="My Plans"
          style={{ letterSpacing: '.3px', marginLeft: 4 }}
        />
        <Spacer space="x2_5" />
        <Flex
          flexDirection="column"
          maxWidth={'800px'}
          outset={{ bottom: 'x3' }}
          rowGap="x3"
        >
          {currentPlan && !currentPlanSubscription && (
            // This should mean that the user has the basic plan
            <CurrentPlanCard
              title={currentPlan.title}
              price={currentPlan.price.description}
              changeAction={{
                title: 'Change Plan',
                onClick: () => setIsChangingSubscription(true),
              }}
              newPlan={changePlanId ? newPlan : undefined}
            />
          )}
          {currentPlanSubscription && (
            <CurrentPlanCard
              title={currentPlan?.title ?? currentPlanSubscription.name}
              price={`$${(currentPlanSubscription.total / 100).toFixed(2)}`}
              currentSubscription={currentPlanSubscription}
              changeAction={{
                title: 'Change Plan',
                onClick: () => setIsChangingSubscription(true),
              }}
              renewAction={
                currentPlanSubscription.isSetToCancel
                  ? {
                      title: 'Cancel Scheduled Change',
                      onClick: () => setIsShowingChangeCancelation(true),
                    }
                  : undefined
              }
              cancelAction={
                !currentPlanSubscription.isSetToCancel || newPlan
                  ? {
                      title: 'Cancel',
                      onClick: () =>
                        setSubscriptionCancelationId(
                          currentPlanSubscription.id,
                        ),
                    }
                  : undefined
              }
              addAddonAction={
                // We only allow the unlimited act add-on on legacy plans
                currentPlan &&
                !currentlySoldPlans.includes(currentPlan.id) &&
                canAddActAddon
                  ? {
                      title:
                        marketingContent?.addUnlimitedActAddonButton.content ??
                        'Add Unlimited ACT Add-on',
                      onClick: () =>
                        setAddingAddonSubscriptionId(
                          currentPlanSubscription.id,
                        ),
                      isDisabled: subscriptionAddonAdd.isLoading,
                    }
                  : undefined
              }
              changeItemAction={{
                title: 'Change',
                onClick: () => setIsChangingSubscription(true),
              }}
              cancelItemAction={{
                title: 'Cancel',
                onClick: itemId => setSubscriptionItemCancelationId(itemId),
                isDisabled: subscriptionAddonRemove.isLoading,
              }}
              newPlan={changePlanId ? newPlan : undefined}
            />
          )}

          {currentAffiliateSubscription && (
            <CurrentPlanCard
              title={currentAffiliateSubscription.name}
              price={`$${(currentAffiliateSubscription.total / 100).toFixed()}`}
              currentSubscription={currentAffiliateSubscription}
              recurrence={
                currentAffiliateSubscription.sku === 'GS00001'
                  ? 'month'
                  : 'year'
              }
              changeAction={
                currentAffiliateSubscription.sku === 'GS00001' ||
                currentAffiliateSubscription.sku === '14757'
                  ? {
                      title: 'Upgrade & Save',
                      onClick: () => setIsChangingAffiliate(true),
                    }
                  : undefined
              }
              renewAction={
                currentAffiliateSubscription.isSetToCancel &&
                currentAffiliateSubscription?.sku !== 'GS00016'
                  ? {
                      title: 'Cancel Scheduled Change',
                      onClick: () =>
                        setIsRenewAffiliateConfirmDialogueOpen(true),
                      isDisabled: isInPromptingsGracePeriod,
                    }
                  : undefined
              }
              cancelAction={
                !currentAffiliateSubscription.isSetToCancel
                  ? {
                      title: 'Cancel',
                      onClick: () =>
                        setSubscriptionCancelationId(
                          currentAffiliateSubscription.id,
                        ),
                    }
                  : undefined
              }
            />
          )}
          <PaymentSettingsCard />
          {/* <PurchasesCard /> */}
        </Flex>
        <UiCard
          title={'My Account Purchases'}
          actions={[
            ...(account.isAffiliate
              ? [
                  {
                    title: 'Promptings PRO',
                    id: 'gs_pro_btn',
                    onClick: () => {
                      window.open(account.promptingProLoginUrl, '_blank')
                    },
                  },
                ]
              : []),

            ...(!account.labels.includes(Label.ShouldHideRevolutionOffice)
              ? [
                  {
                    title: account.isAffiliate
                      ? 'Revolution Office'
                      : 'Invoice History',
                    id: 'revolution_office_btn',
                    onClick: () => {
                      window.open(`${LEGACY_SITE_URL}/bd/backoffice`, '_blank')
                    },
                  },
                ]
              : []),
          ]}
          buttonColor={vertical.id as ButtonColorChoices}
        >
          <Addons />

          <AccountSubscriptionChanger
            shouldHideCurrentPlan={true}
            subscriptionPlanIds={[{ id: '165' }]}
            isOpen={isChangingAffiliate}
            onClose={() => setIsChangingAffiliate(false)}
            isChangingAffiliate={isChangingAffiliate}
          />

          <AccountSubscriptionChanger
            subscriptionPlanIds={subscriptionIds}
            isOpen={isChangingSubscription}
            onClose={() => setIsChangingSubscription(false)}
          />
          {isMobile && (
            <Flex flexDirection="row" justifyContent="center">
              <Anchor
                isDecorated={true}
                color="anchorBlue"
                onClick={() => actions.openPricing()}
              >
                Pricing
              </Anchor>
            </Flex>
          )}
        </UiCard>
        <ConfirmDialog
          isOpen={isRenewAffiliateConfirmDialogueOpen}
          title={`Renew your ${AFFILIATE_BRANDING.capitalized} subscription`}
          description={`Add the ${AFFILIATE_BRANDING.capitalized} subscription of $14/month to my account so I can start my business today.`}
          accept={{
            title: 'Confirm renewal',
            onClick: () => {
              cancelOrUncancelSubscription(
                currentAffiliateSubscription?.id ?? '',
                false,
              )
              cancelSubscription(
                { id: currentAffiliateSubscription?.id ?? '' },
                {
                  onSuccess(data) {
                    // TODO: React Query Refactor, once redux account is removed, invalidate the account query here
                    actions.updatedAccount(
                      data.cancelStripeSubscription.account,
                    )
                  },
                  onError(error) {
                    console.log(error)
                  },
                },
              )
              setIsRenewAffiliateConfirmDialogueOpen(false)
              setIsChangingSubscription(false)
            },
          }}
          decline={{
            title: 'Cancel',
            onClick: () => {
              setIsRenewAffiliateConfirmDialogueOpen(false)
            },
          }}
          zIndex={9000}
          primaryAction={'accept'}
          onClose={() => setIsRenewAffiliateConfirmDialogueOpen(false)}
        />
      </Flex>
      <ConfirmDialog
        isOpen={!!subscriptionCancelationId}
        title="Cancel Subscription"
        description="Are you sure you want to cancel this subscription?"
        decline={{
          title: 'Keep Subscription',
          onClick: () => setSubscriptionCancelationId(undefined),
        }}
        accept={{
          title: 'Cancel Subscription',
          onClick: () => {
            if (!subscriptionCancelationId) return
            cancelOrUncancelSubscription(subscriptionCancelationId, true)
            setSubscriptionCancelationId(undefined)
          },
        }}
        zIndex={1}
      />
      {subscriptionItemCancelationId && (
        <ConfirmDialog
          isOpen={true}
          title="Cancel Addon"
          description="Are you sure you want to cancel this addon?"
          decline={{
            title: 'Keep Addon',
            onClick: () => setSubscriptionItemCancelationId(undefined),
          }}
          accept={{
            title: 'Cancel Addon',
            onClick: async () => {
              await subscriptionAddonRemove.mutateAsync({
                subscriptionItemId: subscriptionItemCancelationId,
              })
              setSubscriptionItemCancelationId(undefined)
            },
            isDisabled: subscriptionAddonRemove.isLoading,
          }}
          zIndex={1}
        />
      )}
      {addingAddonToSubscriptionId && (
        <ConfirmDialog
          isOpen={true}
          title={
            marketingContent?.addUnlimitedActAddonConfirmationTitle.content ??
            'Add Unlimited ACT Add-on'
          }
          description={
            marketingContent?.addUnlimitedActAddonConfirmationDescription
              .content ??
            'Do you want to add the Unlimited ACT Add-on for $10 / month?'
          }
          primaryAction="accept"
          accept={{
            title:
              marketingContent?.addUnlimitedActAddonConfirmationAccept
                .content ?? 'Yes',
            onClick: async () => {
              await subscriptionAddonAdd.mutateAsync({
                subscriptionId: addingAddonToSubscriptionId,
                sku: ACT_ADDON_STRIPE_ID,
              })
              setAddingAddonSubscriptionId(undefined)
            },
            isDisabled: subscriptionAddonAdd.isLoading,
          }}
          decline={{
            title:
              marketingContent?.addUnlimitedActAddonConfirmationDecline
                .content ?? 'No',
            onClick: () => setAddingAddonSubscriptionId(undefined),
          }}
          zIndex={1}
        />
      )}
      <ConfirmDialog
        isOpen={isShowingChangeCancelation}
        title={`Keep my ${currentPlan?.price.description} ${currentPlan?.title} Subscription`}
        description="Click below to keep your plan and continue utilizing the features you enjoy."
        decline={{
          title: 'Close',
          onClick: () => setIsShowingChangeCancelation(false),
        }}
        accept={{
          title: 'Keep Current Plan!',
          onClick: () => {
            cancelOrUncancelSubscription(
              currentPlanSubscription?.id ?? '',
              false,
            )
            setIsShowingChangeCancelation(false)
          },
        }}
        primaryAction="accept"
        zIndex={1}
      />
      <Dialog isOpen={isLoading}>
        <Text
          type="largeBody"
          content={'Confirming your Subscription Changes...'}
        />
      </Dialog>
    </Flex>
  ) : (
    <Text type="body" content="login to view account" />
  )
}
